import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

export enum DialogType {
  ACCIDENT = 'accident',
  INJURY = 'injury',
}

export interface MinimizedDialogState {
  accident: boolean;
  injury: boolean;
}

export interface DialogPosition {
  right: string;
  bottom: string;
}

@Injectable({
  providedIn: 'root',
})
export class DialogPositionService {
  private minimizedDialogsSubject = new BehaviorSubject<MinimizedDialogState>({
    accident: false,
    injury: false,
  });

  public minimizedDialogs$ = this.minimizedDialogsSubject.asObservable();

  // Subject to emit when screen size changes
  private screenResizeSubject = new BehaviorSubject<void>(undefined);
  public screenResize$ = this.screenResizeSubject.asObservable();

  // Maximum screen width to consider a mobile device
  private readonly MOBILE_MAX_WIDTH = 768;

  // Counter to know how many dialogs are minimized
  private minimizedCount = 0;

  constructor(private ngZone: NgZone) {
    // Listen for window size changes
    this.ngZone.runOutsideAngular(() => {
      fromEvent(window, 'resize')
        .pipe(debounceTime(200))
        .subscribe(() => {
          this.ngZone.run(() => {
            this.screenResizeSubject.next();
          });
        });
    });
  }

  /**
   * Updates the minimization state for a specific dialog type
   */
  setDialogMinimized(type: DialogType, isMinimized: boolean): void {
    const currentState = this.minimizedDialogsSubject.value;
    const wasMinimized =
      type === DialogType.ACCIDENT ? currentState.accident : currentState.injury;

    // Update minimized dialogs counter
    if (isMinimized && !wasMinimized) {
      this.minimizedCount++;
    } else if (!isMinimized && wasMinimized) {
      this.minimizedCount--;
    }

    if (type === DialogType.ACCIDENT) {
      this.minimizedDialogsSubject.next({
        ...currentState,
        accident: isMinimized,
      });
    } else if (type === DialogType.INJURY) {
      this.minimizedDialogsSubject.next({
        ...currentState,
        injury: isMinimized,
      });
    }
  }

  /**
   * Gets the horizontal position for a specific minimized dialog
   * based on which other dialogs are currently minimized
   */
  getPositionForDialog(type: DialogType): DialogPosition {
    const state = this.minimizedDialogsSubject.value;
    const isMobile = window.innerWidth <= this.MOBILE_MAX_WIDTH;

    // Check if both dialogs are minimized
    const bothMinimized = state.accident && state.injury;

    if (isMobile) {
      if (bothMinimized) {
        // In mobile with both dialogs minimized, position them one above the other
        if (type === DialogType.ACCIDENT) {
          return { right: '20px', bottom: '50px' }; // Accident above
        } else {
          return { right: '20px', bottom: '0px' }; // Injury below
        }
      } else {
        // In mobile with one dialog minimized, always position it in the same place
        return { right: '20px', bottom: '0px' };
      }
    } else {
      // On desktop
      if (bothMinimized) {
        // With both dialogs minimized, position them side by side
        if (type === DialogType.ACCIDENT) {
          return { right: '280px', bottom: '0px' }; // Accident to the left
        } else {
          return { right: '20px', bottom: '0px' }; // Injury to the right
        }
      } else {
        // With one dialog minimized, always position it aligned to the right
        return { right: '20px', bottom: '0px' };
      }
    }
  }

  /**
   * Checks if there is any minimized dialog
   */
  get hasMinimizedDialogs(): boolean {
    return this.minimizedCount > 0;
  }

  /**
   * Returns the current minimization state
   */
  get hasActiveDialogs(): boolean {
    const state = this.minimizedDialogsSubject.value;
    return !state.accident || !state.injury;
  }

  /**
   * Checks if there is any active dialog (not minimized)
   */
  get currentState(): MinimizedDialogState {
    return this.minimizedDialogsSubject.value;
  }
}
